import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { AUTH0_DOMAIN, AUTH0_DOCTOR_CLIENT_ID, AUTH0_PARENT_CLIENT_ID } from '@/lib/auth0-config';

export function Logout() {
  const { t } = useTranslation();
  const { clearAuth, userType } = useAuthStore();
  const hasLoggedOut = useRef(false);
  const capturedUserType = useRef<string | null>(null);

  useEffect(() => {
    // Prevent double logout
    if (hasLoggedOut.current) return;
    hasLoggedOut.current = true;

    // Capture userType BEFORE clearing anything - check all possible storage keys
    let effectiveUserType = userType || localStorage.getItem('userType');
    
    // Also check the current URL path to determine user type
    if (!effectiveUserType && globalThis.location.pathname.includes('/doctor')) {
      effectiveUserType = 'doctor';
    }
    
    // Fallback checks
    if (!effectiveUserType) {
      if (localStorage.getItem('userType_doctor') === 'doctor') effectiveUserType = 'doctor';
      else if (localStorage.getItem('userType_parent') === 'parent') effectiveUserType = 'parent';
    }
    
    // If still not set, default to parent
    if (!effectiveUserType) effectiveUserType = 'parent';
    
    capturedUserType.current = effectiveUserType;
    
    console.log('[Logout] Detected user type:', effectiveUserType);

    // Clear all local/session storage and cookies
    clearAuth();
    
    // Clear all localStorage keys (including Auth0 cache)
    const keysToRemove = Object.keys(localStorage);
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Clear all cookies (including Auth0 session cookies)
    document.cookie.split(';').forEach(function(c) {
      document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
    });
    
    // Also clear cookies for the root domain
    const domain = globalThis.location.hostname;
    document.cookie.split(';').forEach(function(c) {
      const cookieName = c.split('=')[0].trim();
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${domain}`;
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    });

    // Also call Auth0 logout to clear Auth0 session and IdP session
    const clientId = capturedUserType.current === 'doctor' ? AUTH0_DOCTOR_CLIENT_ID : AUTH0_PARENT_CLIENT_ID;
    const returnTo = capturedUserType.current === 'doctor' 
      ? `${globalThis.location.origin}/doctor/login`
      : globalThis.location.origin;
    const logoutUrl = `https://${AUTH0_DOMAIN}/v2/logout?client_id=${clientId}&returnTo=${encodeURIComponent(returnTo)}`;
    // Use assignment after a short delay to allow storage cleanup
    setTimeout(() => { globalThis.location.href = logoutUrl; }, 50);
  }, [userType, clearAuth]); // Include dependencies

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <h1 className="text-xl font-semibold text-gray-700">{t('auth.loggingOut')}</h1>
        <p className="text-gray-500 mt-2">{t('auth.redirecting')}</p>
      </div>
    </div>
  );
}

